import React, { useState, useEffect } from 'react';
import { FaBarcode, FaQrcode, FaBox, FaWineBottle } from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../ui/Loader';
import Message from '../ui/Message';

const InboundBarcodeDetails = ({ inboundId }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBoxes, setExpandedBoxes] = useState(new Set());

  useEffect(() => {
    fetchInboundDetails();
  }, [inboundId]);

  const fetchInboundDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inventory/inbound/${inboundId}/details`);
      setDetails(response.data);
    } catch (error) {
      console.error('Error fetching inbound details:', error);
      setError('获取入库详情失败');
    } finally {
      setLoading(false);
    }
  };

  const toggleBoxExpansion = (boxId) => {
    const newExpanded = new Set(expandedBoxes);
    if (newExpanded.has(boxId)) {
      newExpanded.delete(boxId);
    } else {
      newExpanded.add(boxId);
    }
    setExpandedBoxes(newExpanded);
  };

  if (loading) return <Loader size="sm" />;
  if (error) return <Message variant="error">{error}</Message>;
  if (!details) return <div className="text-gray-500">暂无条码详情</div>;

  return (
    <div className="space-y-4">
      {/* 入库汇总信息 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 font-medium">总箱数:</span>
            <span className="ml-2">{details.boxes?.length || 0} 箱</span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">总瓶数:</span>
            <span className="ml-2">{details.total_units} 瓶</span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">剩余:</span>
            <span className="ml-2">{details.remaining_units} 瓶</span>
          </div>
          <div>
            <span className="text-blue-700 font-medium">状态:</span>
            <span className="ml-2">
              {details.remaining_units > 0 ? '有库存' : '已售完'}
            </span>
          </div>
        </div>
      </div>

      {/* 箱级别详情 */}
      <div className="space-y-3">
        {details.boxes && details.boxes.map((box) => (
          <div key={box.id} className="border rounded-lg overflow-hidden">
            {/* 箱头部信息 */}
            <div 
              className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
              onClick={() => toggleBoxExpansion(box.id)}
            >
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <FaBox className="mr-2 text-gray-600" />
                  <span className="font-medium">第{box.box_index}箱</span>
                </div>
                
                <div className="text-sm text-gray-600">
                  {box.remaining_units}/{box.units_count} 剩余
                </div>
                
                <div className={`px-2 py-1 rounded-full text-xs ${
                  box.status === 'full' ? 'bg-green-100 text-green-700' :
                  box.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {box.status === 'full' ? '满箱' : 
                   box.status === 'partial' ? '散货' : '已空'}
                </div>
              </div>
              
              <div className="text-gray-400">
                {expandedBoxes.has(box.id) ? '▼' : '▶'}
              </div>
            </div>

            {/* 箱详情内容 */}
            {expandedBoxes.has(box.id) && (
              <div className="p-4 border-t bg-white">
                {/* 箱码信息 - 散货不显示箱码 */}
                {box.status !== 'partial' && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <h5 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                      <FaBox className="mr-2" />
                      箱码信息
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {box.box_barcode && (
                        <div className="flex items-center p-2 bg-white rounded border">
                          <FaBarcode className="mr-2 text-blue-500 flex-shrink-0" />
                          <span className="font-mono text-sm break-all">{box.box_barcode}</span>
                        </div>
                      )}
                      {box.box_qrcode && (
                        <div className="flex items-center p-2 bg-white rounded border">
                          <FaQrcode className="mr-2 text-green-500 flex-shrink-0" />
                          <span className="font-mono text-sm break-all">{box.box_qrcode}</span>
                        </div>
                      )}
                      {!box.box_barcode && !box.box_qrcode && (
                        <div className="text-gray-500 italic">暂无箱码信息</div>
                      )}
                    </div>
                  </div>
                )}

                {/* 单元码信息 */}
                <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                  <h5 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                    <FaWineBottle className="mr-2" />
                    单元码信息 ({box.units?.length || 0} 瓶)
                  </h5>
                  
                  {box.units && box.units.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {box.units.map((unit) => (
                        <div key={unit.id} className="p-3 bg-white border rounded">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-sm">第{unit.unit_index}瓶</span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              unit.status === 'available' ? 'bg-green-100 text-green-700' :
                              unit.status === 'sold' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {unit.status === 'available' ? '可用' : 
                               unit.status === 'sold' ? '已售' : '不可用'}
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            {unit.unit_barcode && (
                              <div className="flex items-center">
                                <FaBarcode className="mr-2 text-blue-400 text-xs flex-shrink-0" />
                                <span className="font-mono text-xs break-all">{unit.unit_barcode}</span>
                              </div>
                            )}
                            
                            {unit.unit_qrcode && (
                              <div className="flex items-center">
                                <FaQrcode className="mr-2 text-green-400 text-xs flex-shrink-0" />
                                <span className="font-mono text-xs break-all">{unit.unit_qrcode}</span>
                              </div>
                            )}
                            
                            {!unit.unit_barcode && !unit.unit_qrcode && (
                              <div className="text-gray-400 italic text-xs">暂无单元码</div>
                            )}
                            
                            {unit.sold_at && (
                              <div className="text-gray-400 text-xs mt-1">
                                售出: {new Date(unit.sold_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic text-center py-4">
                      该箱暂无单元记录
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        
        {(!details.boxes || details.boxes.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            该入库记录暂无条码信息
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundBarcodeDetails;